import { useCallback, useState } from 'react'
import { supabase } from '../lib/supabaseClient'

// On-demand translator for free-form text snippets — digests, AI
// summaries, anything we'd otherwise have re-generated in a second
// language. Calls the `translate-text` edge function which is backed
// by Microsoft Translator, so it doesn't burn Gemini/Groq/Cerebras
// quota that the LLM router shares with article generation.
//
// State is per-mount (no global cache): the typical flow is open
// surface → tap Translate → read. Re-mounts re-translate, which is
// cheap on MS Translator and avoids stale cache concerns.
export function useTextTranslation() {
  const [translation, setTranslation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const translate = useCallback(async (text, targetLang) => {
    if (!text || !targetLang) return null
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await supabase.functions.invoke('translate-text', {
        body: { strings: [text], target_lang: targetLang },
      })
      if (err) {
        let detail = err.message || 'translation failed'
        const inner = err.context
        if (inner && typeof inner.text === 'function') {
          try {
            const raw = await inner.text()
            try {
              const parsed = JSON.parse(raw)
              if (parsed?.error) detail = parsed.error
              if (parsed?.detail) detail += ` — ${parsed.detail}`
            } catch { /* not JSON */ }
          } catch { /* ignore */ }
        }
        throw new Error(detail)
      }
      const out = data?.translations?.[0]
      if (typeof out !== 'string' || !out) throw new Error('empty translation')
      setTranslation(out)
      return out
    } catch (e) {
      setError(e?.message || 'translation failed')
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setTranslation(null)
    setError(null)
  }, [])

  return { translation, loading, error, translate, reset }
}
